import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { useMovie, useSimilar } from "@/hooks/useMovies";
import { SmartImage } from "@/components/movie/SmartImage";
import { MovieRow } from "@/components/movie/MovieRow";
import { MovieDetailSkeleton } from "@/components/skeleton/Skeletons";
import { ErrorState, EmptyState } from "@/components/common/States";
import { Page, Reveal } from "@/components/common/Reveal";
import { GlassButton, GlassModal } from "@/components/glass/Glass";
import { WatchlistButton } from "@/components/movie/WatchlistButton";
import { Dot, QualityBadge, Rating } from "@/components/movie/MovieMeta";
import { IconChevronLeft, IconFilm, IconPlay, IconShare, IconClock } from "@/components/icons/Icons";
import { formatDuration, initials, youtubeEmbed } from "@/lib/format";
import { clearSharedOrigin, takeSharedOrigin } from "@/lib/shared";
import { useSeo } from "@/lib/seo";
import { EpisodeList } from "@/components/movie/EpisodeList";
import { AdSlot } from "@/components/common/Ads";
import { useProgress } from "@/hooks/useVideo";
import { useAuth } from "@/context/AuthContext";

/** Poster that FLIP-animates from the clicked card's rect (shared element). */
function SharedPoster({ movie }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const el = ref.current;
    const from = takeSharedOrigin(movie.id);
    if (!el || !from || reduce) return;
    const to = el.getBoundingClientRect();
    if (!to.width) return;
    const x = from.left - to.left;
    const y = from.top - to.top;
    const sx = from.width / to.width;
    const sy = from.height / to.height;
    if (!el.animate) return;
    el.style.transformOrigin = "0 0";
    el.style.zIndex = "30";
    const anim = el.animate(
      [
        { transform: `translate(${x}px, ${y}px) scale(${sx}, ${sy})`, borderRadius: "20px" },
        { transform: "translate(0px, 0px) scale(1, 1)", borderRadius: "22px" },
      ],
      { duration: 640, easing: "cubic-bezier(.2,.9,.2,1)", fill: "backwards" }
    );
    const done = () => {
      if (el) el.style.zIndex = "";
    };
    anim.onfinish = () => {
      done();
      clearSharedOrigin();
    };
    return () => {
      anim.cancel();
      done();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie.id]);

  return (
    <div ref={ref} className="relative mx-auto aspect-[2/3] w-[52vw] max-w-[270px] shrink-0 overflow-hidden rounded-[22px] shadow-[0_30px_80px_rgba(0,0,0,.6)] ring-1 ring-white/10 will-change-transform md:mx-0 md:w-[240px] lg:w-[270px]" data-testid="movie-detail-poster">
      <SmartImage src={movie.poster} alt={`${movie.title} posteri`} priority fallbackTitle={movie.title} className="absolute inset-0" />
    </div>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[12.5px] font-medium text-white/45">{label}</dt>
      <dd className="mt-0.5 text-[14.5px] font-semibold text-white/90">{value}</dd>
    </div>
  );
}

export default function MovieDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: m, isLoading, isError, error, refetch, isPlaceholderData } = useMovie(id);
  const { data: similar } = useSimilar(m && !isPlaceholderData ? id : null);
  const [trailer, setTrailer] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();
  const { data: progress } = useProgress(id, Boolean(user));

  const jsonLd = useMemo(
    () =>
      m && {
        "@context": "https://schema.org",
        "@type": m.type === "series" ? "TVSeries" : "Movie",
        name: m.title,
        description: m.description || undefined,
        image: m.poster || undefined,
        datePublished: m.year ? String(m.year) : undefined,
        genre: m.genres.map((g) => g.name),
        duration: m.duration ? `PT${m.duration}M` : undefined,
        director: m.director ? { "@type": "Person", name: m.director } : undefined,
        actor: m.cast.slice(0, 10).map((c) => ({ "@type": "Person", name: c.name })),
        aggregateRating: m.rating ? { "@type": "AggregateRating", ratingValue: m.rating, bestRating: 10, ratingCount: Math.max(1, m.views) } : undefined,
      },
    [m]
  );
  useSeo({ title: m?.title, description: m?.description, image: m?.backdrop || m?.poster, type: "video.movie", jsonLd });

  if (isLoading && !m) return <MovieDetailSkeleton />;
  if (isError && !m) {
    return (
      <Page className="pk-pb-dock pt-32">
        {error?.status === 404 ? (
          <EmptyState icon={IconFilm} title="Film topilmadi" text="Bu film o‘chirilgan yoki havola noto‘g‘ri." action={<Link to="/"><GlassButton>Bosh sahifaga</GlassButton></Link>} testId="movie-not-found" />
        ) : (
          <ErrorState error={error} onRetry={refetch} />
        )}
      </Page>
    );
  }
  if (!m) return null;

  const play = () => {
    if (!m.hasVideo) return toast("Video hozircha mavjud emas.");
    navigate(`/watch/${m.id}`);
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: m.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Havola nusxalandi");
      }
    } catch (_) {}
  };
  const resumeEp = m.type === "series" && progress?.episode_id ? m.seasons.flatMap((x) => x.episodes).find((e) => e.id === progress.episode_id) : null;
  const resumeLabel = resumeEp ? `Davom ettirish · ${resumeEp.number}-qism` : progress?.position > 30 ? "Davom ettirish" : "Tomosha qilish";
  const embed = youtubeEmbed(m.trailerUrl);
  const longDesc = (m.description || "").length > 280;

  return (
    <main className="pk-pb-dock" data-testid="movie-detail">
      {/* backdrop */}
      <div className="relative h-[50svh] min-h-[340px] max-h-[640px] w-full overflow-hidden md:h-[62svh]">
        <motion.div initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.2, 0.9, 0.2, 1] }} className="absolute inset-0">
          <SmartImage src={m.backdrop || m.poster} alt="" priority variant="backdrop" className="absolute inset-0" imgClassName={!m.backdrop ? "scale-125 blur-2xl" : ""} />
        </motion.div>
        <div className="pk-vignette absolute inset-0" />
        <div className="pk-fade-bottom absolute inset-x-0 bottom-0 h-full" />
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} aria-label="Orqaga" className="pk-glass absolute left-4 z-10 grid h-11 w-11 place-items-center rounded-full text-white lg:top-24 lg:left-10" style={{ top: "calc(64px + var(--safe-top))" }} data-testid="movie-detail-back-button">
          <IconChevronLeft size={22} />
        </button>
      </div>

      <div className="pk-container relative z-[2] -mt-[34svh] md:-mt-56">
        <div className="flex flex-col gap-7 md:flex-row md:items-end md:gap-10">
          <SharedPoster movie={m} />
          <Reveal className="min-w-0 flex-1 text-center md:text-left" delay={0.08}>
            {m.type === "series" && <span className="pk-glass mb-3 inline-flex h-7 items-center rounded-full px-3 text-[12px] font-semibold">Serial</span>}
            <h1 className="text-[32px] font-extrabold leading-[1.05] tracking-[-0.03em] text-white sm:text-[44px] lg:text-[56px]" data-testid="movie-detail-title">
              {m.title}
            </h1>
            {m.originalTitle && m.originalTitle !== m.title && <p className="mt-1 text-[15px] font-medium text-white/50">{m.originalTitle}</p>}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2 text-[14.5px] font-medium text-white/75 md:justify-start" data-testid="movie-detail-meta">
              <Rating value={m.rating} className="text-[14.5px]" size={14} />
              {m.rating !== null && (m.year || m.duration) && <Dot />}
              {m.year && <span>{m.year}</span>}
              {m.duration && (
                <>
                  <Dot />
                  <span className="inline-flex items-center gap-1">
                    <IconClock size={14} /> {formatDuration(m.duration)}
                  </span>
                </>
              )}
              {m.ageRating && <QualityBadge>{m.ageRating}</QualityBadge>}
              {m.quality.map((q) => (
                <QualityBadge key={q}>{q}</QualityBadge>
              ))}
            </div>
            {m.genres.length > 0 && (
              <div className="mt-4 flex flex-wrap justify-center gap-2 md:justify-start">
                {m.genres.map((g) => (
                  <Link key={g.slug} to={`/category/${g.slug}`} className="rounded-full border border-white/10 bg-white/[.05] px-3 py-1 text-[13px] font-semibold text-white/80 transition-colors hover:bg-white/[.1] hover:text-white" data-testid={`movie-genre-${g.slug}`}>
                    {g.name}
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3 md:justify-start">
              <GlassButton variant="primary" size="lg" onClick={play} data-testid="movie-detail-play-button" className="min-w-[190px]">
                <IconPlay size={18} /> {resumeLabel}
              </GlassButton>
              <WatchlistButton movieId={m.id} testId="movie-detail-watchlist-button" />
              {m.trailerUrl && (
                <GlassButton size="lg" onClick={() => setTrailer(true)} data-testid="movie-detail-trailer-button">
                  <IconFilm size={19} /> Treyler
                </GlassButton>
              )}
              <GlassButton size="icon" onClick={share} aria-label="Ulashish" data-testid="movie-detail-share-button" className="h-12 w-12">
                <IconShare size={19} />
              </GlassButton>
            </div>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Reveal delay={0.15}>
            <h2 className="text-[18px] font-bold tracking-[-0.01em] text-white">{m.type === "series" ? "Serial haqida" : "Film haqida"}</h2>
            {m.description ? (
              <>
                <p className={`mt-3 max-w-[70ch] whitespace-pre-line text-[15.5px] leading-[1.7] text-white/70 ${!expanded && longDesc ? "line-clamp-3" : ""}`} data-testid="movie-detail-description">
                  {m.description}
                </p>
                {longDesc && (
                  <button onClick={() => setExpanded((v) => !v)} className="mt-2 text-[14px] font-semibold text-white hover:underline" data-testid="movie-detail-more-button">
                    {expanded ? "Kamroq" : "Ko‘proq"}
                  </button>
                )}
              </>
            ) : (
              <p className="mt-3 text-[15px] text-white/45">Tavsif hozircha qo‘shilmagan.</p>
            )}
          </Reveal>
          {(m.director || m.country || m.language || m.year) && (
            <Reveal delay={0.2}>
              <dl className="pk-surface grid grid-cols-2 gap-4 rounded-[20px] p-5">
                <Fact label="Rejissyor" value={m.director} />
                <Fact label="Yil" value={m.year} />
                <Fact label="Mamlakat" value={m.country} />
                <Fact label="Til" value={m.language} />
              </dl>
            </Reveal>
          )}
        </div>

        {m.type === "series" && m.seasons.length > 0 && <EpisodeList movie={m} currentEpisodeId={progress?.episode_id} />}

        {m.cast.length > 0 && (
          <section className="mt-10" data-testid="movie-cast">
            <h2 className="text-[18px] font-bold tracking-[-0.01em] text-white">Rollarda</h2>
            <div className="pk-scroll-x -mx-4 mt-4 flex gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
              {m.cast.map((c, i) => (
                <Reveal key={`${c.name}-${i}`} delay={Math.min(i, 8) * 0.04} className="w-[92px] shrink-0 text-center">
                  <div className="mx-auto h-[76px] w-[76px] overflow-hidden rounded-full bg-white/[.07] ring-1 ring-white/10">
                    {c.photo ? <SmartImage src={c.photo} alt={c.name} className="h-full w-full rounded-full" /> : <span className="grid h-full w-full place-items-center text-[20px] font-semibold text-white/70">{initials(c.name)}</span>}
                  </div>
                  <p className="mt-2 line-clamp-2 text-[13px] font-semibold leading-tight text-white">{c.name}</p>
                  {c.role && <p className="mt-0.5 line-clamp-1 text-[12px] text-white/45">{c.role}</p>}
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </div>

      <AdSlot name="banner_detail" className="mt-8" />
      {similar?.length > 0 && (
        <div className="mt-8">
          <MovieRow title="O‘xshash filmlar" items={similar} testId="row-similar" />
        </div>
      )}

      <GlassModal open={trailer} onClose={() => setTrailer(false)} label="Treyler" testId="trailer-modal" className="max-w-5xl">
        <div className="aspect-video w-full bg-black">
          {embed ? (
            <iframe src={embed} title={`${m.title} treyleri`} className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
          ) : (
            <video src={m.trailerUrl} controls autoPlay playsInline preload="metadata" className="h-full w-full" />
          )}
        </div>
      </GlassModal>
    </main>
  );
}
